import { CircleAlert } from "lucide-react";

import type { AgenticLogicActiveRepairRun } from "@intelligems/ig-types/dtos/agentic-logic/repair";
import {
  IgBorderColors,
  IgCoreTextColors,
} from "@intelligems/ig-design-tokens/constants";
import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgPaper } from "@intelligems/ig-theme/IgPaper";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Stack } from "@mui/material";
import { useRestoreAgenticLogicRepairBaseline } from "@src/api/agentic-logic/restore-agentic-logic-repair-baseline";

/**
 * The consent surface for a HELD repair: the checks went green, but only
 * because the loop changed what the rule does.
 *
 * A sibling of `RepairGiveUpPanel` rather than a branch inside it, because the
 * give-up framing does not bend: nothing gave up, nothing was stood down, and
 * the rule is not broken. What is true here — and true nowhere else on the
 * surface — is that a rewrite nobody approved is sitting in the draft while
 * the merchant's own published version goes on serving. Both affordances are
 * existing rule actions, so this panel needs no endpoint of its own:
 * `publish` accepts the rewrite, `restore-repair-baseline` rejects it.
 */
export const RepairApprovalPanel = ({
  ruleId,
  run,
  authorName,
  isPublishing,
  isPublishDisabled = false,
  advancedEnabled,
  onPublish,
  onOpenCodeWorkspace,
  embedded = false,
  hasPublishedVersion = true,
}: {
  ruleId: string;
  run: AgenticLogicActiveRepairRun;
  authorName: string;
  isPublishing: boolean;
  isPublishDisabled?: boolean;
  /**
   * The org's Advanced verdict. A prop, not a hook: this panel is pure copy
   * around two existing rule actions and is rendered directly by its spec.
   */
  advancedEnabled: boolean;
  onPublish: () => void;
  onOpenCodeWorkspace: () => void;
  embedded?: boolean;
  hasPublishedVersion?: boolean;
}) => {
  const restoreBaseline = useRestoreAgenticLogicRepairBaseline();
  const pending = run.pendingApproval;

  // The model's own summary of what it changed. Absent only when the attempt
  // record predates this field or the rewrite carried no summary — say less
  // rather than guess, but never imply the change was cosmetic.
  const changeSummary =
    pending?.changeSummary ??
    "the rule's own logic — the platform's record of the rewrite is incomplete";

  return (
    <IgPaper
      component={embedded ? "div" : "section"}
      role={embedded ? undefined : "region"}
      aria-label={embedded ? undefined : "Waiting for your approval"}
      sx={{
        border: "none",
        borderBottom: embedded
          ? "none"
          : `1px solid ${IgBorderColors.core.default}`,
        borderLeft: embedded
          ? "none"
          : `3px solid ${IgCoreTextColors.tonal.warning}`,
        borderRadius: 0,
        boxShadow: "none",
        p: 0,
      }}
    >
      <Stack spacing={2} sx={embedded ? {} : { px: 2.5, py: 1.5 }}>
        {!embedded ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircleAlert
              size={16}
              color={IgCoreTextColors.tonal.warning}
              aria-hidden
            />
            <IgTypography variant="body2" sx={{ fontWeight: 600 }}>
              {run.giveUp?.cause === "manual_publish"
                ? "The repaired draft passed its checks. Apply the changes when ready."
                : `${authorName} could only pass the checks by changing what this rule does.`}
            </IgTypography>
          </Stack>
        ) : null}
        <IgTypography variant="body2">
          {embedded ? "Proposed change: " : `${authorName} changed `}
          {changeSummary}{" "}
          {embedded
            ? hasPublishedVersion
              ? "Your published rule is unchanged."
              : "This rule has not been published."
            : advancedEnabled
              ? "Your published version is still serving — nothing shipped."
              : // Same fact, no version pointer: what a merchant needs to know is
                // that the change is NOT live and their shoppers are unaffected.
                "Your live rule is unchanged — nothing shipped."}
        </IgTypography>
        {!embedded && pending?.construct ? (
          <IgTypography variant="body2">
            <strong>Why:</strong> the checks refused{" "}
            <code>{pending.construct}</code>
            {pending.remedy ? `. ${pending.remedy}` : "."}
          </IgTypography>
        ) : null}
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
          <IgButton
            size="small"
            variant="contained"
            disabled={
              isPublishing || isPublishDisabled || restoreBaseline.isPending
            }
            // Called with NO arguments: the handler the page passes takes
            // optional publish notes, and forwarding onClick's event would hand
            // a MouseEvent to a parameter typed `string | undefined`.
            onClick={() => onPublish()}
          >
            {embedded
              ? "Apply changes"
              : advancedEnabled
                ? "Apply changes"
                : // The verb is the merchant's decision, not the platform's: they
                  // are accepting a change, and "publish" names a step the
                  // platform otherwise takes for them.
                  `Apply ${authorName}'s change`}
          </IgButton>
          {/* The code workspace is the Advanced tab, which a basic org cannot
              reach — a button that navigates nowhere is worse than no button. */}
          {advancedEnabled ? (
            <IgButton size="small" variant="text" onClick={onOpenCodeWorkspace}>
              Review the code
            </IgButton>
          ) : null}
          {run.baselineDraftS3Key ? (
            <IgButton
              size="small"
              variant="outlined"
              disabled={
                restoreBaseline.isPending || isPublishing || isPublishDisabled
              }
              onClick={() =>
                restoreBaseline.mutate({
                  body: undefined,
                  pathParams: { ruleId },
                  queryParams: undefined,
                })
              }
            >
              {embedded
                ? "Keep previous logic"
                : advancedEnabled
                  ? `Restore the version before ${authorName}'s fixes`
                  : `Discard ${authorName}'s change`}
            </IgButton>
          ) : null}
        </Stack>
      </Stack>
    </IgPaper>
  );
};
